import { AreaEdit, SubRegionAction, SubRegionRef } from '../rules/suburb-subregion-edits';

/**
 * The suburb map's vocabulary (#256), shared by the engine's modules and the workspace. Framework-free: nothing here
 * knows about Angular.
 */

/**
 * View: a click selects (opens the suburb's panel), nothing is edited. The others edit on click, paint and lasso.
 * Sub-region (#260): a click puts a suburb in the active sub-region or takes it out; paint and lasso follow Add / Remove.
 */
export type MapMode = 'view' | 'assign' | 'erase' | 'cover' | 'subregion';
export type MapTool = 'pointer' | 'lasso' | 'polygon';
/** A click toggles; a sweep (paint, lasso, polygon, grow) only ever goes one way. */
export type Gesture = 'click' | 'sweep';
export type Rgb = [number, number, number];

export interface MapRegion {
  id: string;
  name: string;
  state: string | null;
  rgb: Rgb;
}

/** A sub-region (#260): drawn as an outline over its region's fill, in its own colour (--app-subregion-n). */
export interface MapSubRegion extends SubRegionRef {
  rgb: Rgb;
}

/** One change to one suburb, as the server takes it (PATCH /website/suburb-areas). */
export type MapEdit = AreaEdit;

export interface MapRow {
  salCode: string;
  regionId: string | null;
  covered: boolean;
  subRegions?: string[];
}

export interface MapPage {
  regionId: string | null;
  label: string;
}

export interface HoverInfo {
  code: string;
  name: string;
  state: string;
  regionId: string | null;
  covered: boolean;
  page: string | null;
  /** The names of the sub-regions it is in. */
  subRegions: string[];
  x: number;
  y: number;
}

/** A crawl place drawn on the selected suburb (#253 step 3), marked by where it stands. */
export interface MapPlace {
  id: number;
  name: string;
  lat: number;
  lng: number;
  /**
   * both: in Conductor and on the website · conductor: a provider, not on the website · website: on the website (not a
   * provider) · pending: waiting for review · hidden: hidden in curation · other · unlinked: a Conductor provider that
   * is a place, not linked to a crawl place yet (its id is negative: provider-markers.ts)
   */
  state: 'both' | 'conductor' | 'website' | 'pending' | 'hidden' | 'other' | 'unlinked';
}

/** A position on the map, for the pin of a place added by hand. */
export interface LngLat {
  lng: number;
  lat: number;
}

/** A point in pixels, relative to the map element. */
export interface ScreenPoint {
  x: number;
  y: number;
}

/** [west, south, east, north] in degrees. */
export type Bounds = [number, number, number, number];

/** Which sub-region an edit is for, and whether it adds or takes out (default: the active one, and its action). */
export interface SubRegionPick {
  subRegionId?: string | null;
  action?: SubRegionAction;
}

export interface EngineHooks {
  /**
   * A user action, already applied locally: the workspace saves it. `before` is every touched suburb as it was, so a
   * save that fails or is cancelled puts the map back exactly (covered flags included).
   */
  onAction(edits: MapEdit[], label: string, before: MapRow[]): void;
  onStatus(message: string, error?: boolean): void;
  onHover(info: HoverInfo | null): void;
  /** Assignments changed (counts, lists). */
  onChange(): void;
  /** A suburb was clicked (in any mode). */
  onSelect(code: string): void;
}

export const AU_STATES = [
  { code: 'nsw', name: 'New South Wales' },
  { code: 'vic', name: 'Victoria' },
  { code: 'qld', name: 'Queensland' },
  { code: 'sa', name: 'South Australia' },
  { code: 'wa', name: 'Western Australia' },
  { code: 'tas', name: 'Tasmania' },
  { code: 'nt', name: 'Northern Territory' },
  { code: 'act', name: 'Australian Capital Territory' },
  { code: 'ot', name: 'Other Territories' },
];

/** The all-states review view: assigned suburbs only, read-only. */
export const OVERVIEW = 'all';
